import React, { createContext, ReactNode } from 'react';
import {
  ConnectOptions,
  CreateLocalTrackOptions,
  LocalAudioTrack,
  LocalVideoTrack,
  Room as TwilioRoom,
  TwilioError,
} from 'twilio-video';
import { Callback, ErrorCallback } from '../../types/twilio';
import useHandleOnDisconnect from './useHandleOnDisconnect/useHandleOnDisconnect';
import useHandleRoomDisconnectionErrors from './useHandleRoomDisconnectionErrors/useHandleRoomDisconnectionErrors';
import useHandleTrackPublicationFailed from './useHandleTrackPublicationFailed/useHandleTrackPublicationFailed';
import useLocalTracks from './useLocalTracks/useLocalTracks';
import useRoom from './useRoom/useRoom';
import { SelectedParticipantProvider } from './useSelectedParticipant/useSelectedParticipant';

/*
 *  The hooks used by the VideoProvider component are different than the hooks found in the 'hooks/' directory. The hooks
 *  in the 'hooks/' directory can be used anywhere in a video application, and they can be used any number of times.
 *  the hooks in the 'VideoProvider/' directory are intended to be used by the VideoProvider component only. Using these hooks
 *  elsewhere in the application may cause problems as these hooks should not be used more than once in an application.
 */

export interface IVideoContext {
  room: TwilioRoom;
  localTracks: (LocalAudioTrack | LocalVideoTrack)[];
  connect: (token: string) => Promise<void>;
  isConnecting: boolean;
  onError: ErrorCallback;
  onDisconnect: Callback;
  getLocalVideoTrack: (facingMode?: CreateLocalTrackOptions['facingMode']) => Promise<LocalVideoTrack>;
}

export const VideoContext = createContext<IVideoContext>(null!);

interface VideoProviderProps {
  options?: ConnectOptions;
  onError: ErrorCallback;
  onDisconnect?: Callback;
  children: ReactNode;
}

export function VideoProvider({ options, children, onError = () => {}, onDisconnect = () => {} }: VideoProviderProps) {
  const onErrorCallback = (error: TwilioError) => {
    console.log(`ERROR: ${error.message}`, error);
    onError(error);
  };

  const { localTracks, getLocalVideoTrack } = useLocalTracks();
  const { room: twilioRoom, isConnecting, connect } = useRoom(localTracks, onErrorCallback, options);

  // Register onError and onDisconnect callback functions.
  useHandleRoomDisconnectionErrors(twilioRoom, onError);
  useHandleTrackPublicationFailed(twilioRoom, onError);
  useHandleOnDisconnect(twilioRoom, onDisconnect);

  return (
    <VideoContext.Provider
      value={{
        room: twilioRoom,
        localTracks,
        isConnecting,
        onError: onErrorCallback,
        onDisconnect,
        connect,
        getLocalVideoTrack,
      }}
    >
      <SelectedParticipantProvider room={twilioRoom}>{children}</SelectedParticipantProvider>
    </VideoContext.Provider>
  );
}
